import { create } from 'zustand';

export interface ToolCall {
  id: string;
  tool: string;
  args?: string;
  status: 'running' | 'done' | 'error';
  // 1-based agentic step index (from the backend's step_number). When one round
  // runs multiple tools in parallel, the terminal tool_result for each open step
  // is addressed by this number, not by a single "most recent" cursor — otherwise
  // an earlier card is orphaned and spins forever.
  stepNumber?: number;
}

export interface WidgetBlock {
  type:
    | 'contact_card'
    | 'map_preview'
    | 'maps_card'
    | 'directions_card'
    | 'weather_card'
    | 'calendar_card'
    | 'landmark_card'
    | 'now_playing_card';
  data: Record<string, unknown>;
}

export interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
  isStreaming?: boolean;
  toolCalls?: ToolCall[];
  thinkingContent?: string;
  isThinkingDone?: boolean;
  widgets?: WidgetBlock[];
}

export interface Conversation {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  message_count: number;
  model?: string;
  model_provider?: string;
  metadata_?: { channel?: string } | null;
}

interface ChatState {
  conversations: Conversation[];
  currentConversation: Conversation | null;
  messages: Message[];
  isStreaming: boolean;

  setConversations: (conversations: Conversation[]) => void;
  setCurrentConversation: (conversation: Conversation | null) => void;
  setMessages: (messages: Message[]) => void;
  addMessage: (message: Message) => void;
  updateMessage: (id: string, content: string | undefined, isStreaming?: boolean) => void;
  appendToMessage: (id: string, chunk: string) => void;
  addToolCall: (messageId: string, toolCall: ToolCall) => void;
  updateToolCall: (messageId: string, toolCallId: string, status: ToolCall['status']) => void;
  // Finalize the card whose stepNumber matches — parallel-safe addressing so a
  // terminal tool_result lands on the right card regardless of arrival order.
  updateToolCallByStep: (messageId: string, stepNumber: number, status: ToolCall['status']) => void;
  setIsStreaming: (isStreaming: boolean) => void;
  addConversation: (conversation: Conversation) => void;
  updateConversation: (id: string, updates: Partial<Conversation>) => void;
  clearMessages: () => void;
}

// Hydrate the active conversation synchronously from localStorage on first
// import so a hard refresh doesn't render the new-chat page for a frame
// before the saved conversation loads. The full record is fetched lazily;
// this placeholder is enough to keep routing on the conversation view.
const _hydrateActiveConversation = (): Conversation | null => {
  try {
    const id = typeof window !== 'undefined'
      ? window.localStorage.getItem('jarvis_active_conversation')
      : null;
    if (!id) return null;
    return {
      id,
      title: '',
      created_at: '',
      updated_at: '',
      message_count: 0,
    };
  } catch {
    return null;
  }
};

export const useChatStore = create<ChatState>((set, get) => ({
  conversations: [],
  currentConversation: _hydrateActiveConversation(),
  messages: [],
  isStreaming: false,

  setConversations: (conversations) => set({ conversations }),

  setCurrentConversation: (conversation) => {
    set({ currentConversation: conversation });
    // Persist so refresh doesn't lose the active chat
    if (conversation?.id) {
      localStorage.setItem('jarvis_active_conversation', conversation.id);
    } else {
      localStorage.removeItem('jarvis_active_conversation');
    }
  },

  setMessages: (messages) => set({ messages }),

  addMessage: (message) =>
    set((state) => ({
      messages: [...state.messages, message],
    })),

  updateMessage: (id, content, isStreaming) =>
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === id
          ? { ...msg, ...(content !== undefined && { content }), isStreaming: isStreaming ?? msg.isStreaming }
          : msg
      ),
    })),

  appendToMessage: (id, chunk) =>
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === id ? { ...msg, content: msg.content + chunk } : msg
      ),
    })),

  addToolCall: (messageId, toolCall) =>
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === messageId
          ? { ...msg, toolCalls: [...(msg.toolCalls || []), toolCall] }
          : msg
      ),
    })),

  updateToolCall: (messageId, toolCallId, status) =>
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === messageId
          ? {
              ...msg,
              toolCalls: (msg.toolCalls || []).map((tc) =>
                tc.id === toolCallId ? { ...tc, status } : tc
              ),
            }
          : msg
      ),
    })),

  updateToolCallByStep: (messageId, stepNumber, status) =>
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === messageId
          ? {
              ...msg,
              toolCalls: (msg.toolCalls || []).map((tc) =>
                tc.stepNumber === stepNumber ? { ...tc, status } : tc
              ),
            }
          : msg
      ),
    })),

  setIsStreaming: (isStreaming) => set({ isStreaming }),

  addConversation: (conversation) =>
    set((state) => ({
      conversations: [conversation, ...state.conversations],
    })),

  updateConversation: (id, updates) =>
    set((state) => ({
      conversations: state.conversations.map((conv) =>
        conv.id === id ? { ...conv, ...updates } : conv
      ),
    })),

  clearMessages: () => set({ messages: [] }),
}));
